import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import CssBaseline from '@mui/material/CssBaseline';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useLocation,
	useParams,
	Navigate
} from 'react-router-dom';
import {Theme} from './types';
import {Helmet} from 'react-helmet';
import ContentWrapper from './containers/ContentWrapper';
import config from './common/config';
import {ThemeProvider} from './ThemeProvider';
import { initJ2S } from 'react-jump2source';
import RepositoryEntryView from './containers/RepositoryEntryView';
import TimelineView from './containers/TimelineView';
import ChartView from './containers/ChartView';
import RepositorySettingsView from './containers/RepositorySettingsView';
import CanvasView from './containers/CanvasView';
import RepositoryEntriesView from './containers/RepositoryEntriesView';
import ReplicateRepositoryView from './containers/ReplicateRepositoryView';
import { SHARED_VIEW_ROUTE_PREFIX } from './common/sharedView';
import { AppHeaderAndPromptsVisibilityProvider } from './contexts/AppHeaderAndPromptsVisibilityContext';
import NotFoundView from './containers/NotFoundView';
import { Analytics } from '@vercel/analytics/react';
import CollectionsView from './containers/CollectionsView';
import ApiKeysView from './containers/ApiKeysView';
import SettingsView from './containers/SettingsView';
import TeamSettingsView from './containers/TeamSettingsView';
import { initializeGA4, updateGA4Consent } from './common/analytics';
import { useCookieConsent } from './contexts/CookieConsentContext';
import CookiePromptDialog from './components/CookiePromptDialog';
import useOnboardingFlow from './hooks/useOnboardingFlow';
import { useAppSelector } from './hooks';
import { selectUserProfile } from './containers/authSlice';

const RepositoryRouteRedirect = () => {
	const { id } = useParams();
	const { search } = useLocation();
	return <Navigate replace to={`/collection/${id}${search}`} />;
};

const Page = styled.div`
  background-color: ${({ theme }) => (theme as Theme).palette.background.default};
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => {
		const mode = (theme as Theme).palette.mode;
		const lightStops = [
			(theme as Theme).palette.background.default,
			(theme as Theme).palette.background.default,
		];
		const darkStops = [
			(theme as Theme).palette.background.default,
			(theme as Theme).palette.background.default,
		];
		const stops = mode === 'dark' ? darkStops : lightStops;
		return `linear-gradient(-45deg, ${stops.join(', ')})`;
	}};
  color: ${({ theme }) => (theme as Theme).palette.text.primary};
`;

function App() {
	// Only initialize react-jump2source in development
	useEffect(() => {
		if (process.env.NODE_ENV === 'development') {
			initJ2S({
				resolver: process.env.REACT_APP_J2S_RESOLVER as any || 'cursor'
			});
		}
	}, []);

	const { analyticsConsent, cookiePromptCompleted } = useCookieConsent();
	const userProfile = useAppSelector(selectUserProfile);
	const {
		isOnboardingRedirectResolved,
		isLandingDemoDecisionOpen,
		landingDemoDecisionVariant,
		keepWorkspace,
		wipeWorkspaceAndLoadDemo,
	} = useOnboardingFlow({ cookiePromptCompleted });

	// Initialize Tracking
	useEffect(() => {
		if (config.ga4MeasurementId && analyticsConsent) {
			initializeGA4(config.ga4MeasurementId);
		}

		if (analyticsConsent) {
			updateGA4Consent(true);
		}
	}, [analyticsConsent, config.ga4MeasurementId]);

	if (!isOnboardingRedirectResolved) {
		return null;
	}

	return (
		<ThemeProvider>
			<Analytics />
			<CssBaseline />
			<Helmet>
				<meta charSet="utf-8" />
				<title>{userProfile?.companyName ?? config.appName}</title>
				<link rel="canonical" href="http://mysite.com/example" />
			</Helmet>
			<Page>
				<Router>
					<AppHeaderAndPromptsVisibilityProvider>
						<Dialog open={isLandingDemoDecisionOpen} fullWidth maxWidth='xs'>
							{landingDemoDecisionVariant === 'cloud' ? (
								<>
									<DialogTitle>Clear workspace first</DialogTitle>
									<DialogContent>
										<Typography variant='body2' color='text.secondary'>
										Your workspace isn&apos;t empty. If you wish to load a new template, clear your workspace first. This rule exists to prevent you from accidentally deleting important data.
										</Typography>
									</DialogContent>
									<DialogActions sx={{ px: 3, pb: 2 }}>
										<Button variant='contained' onClick={keepWorkspace} fullWidth>
										Got it
										</Button>
									</DialogActions>
								</>
							) : (
								<>
									<DialogTitle>Reset workspace?</DialogTitle>
									<DialogContent>
										<Typography variant='body2' color='text.secondary'>
										Your offline workspace already has some data. Loading a new template will delete it. Do you want to continue?
										</Typography>
										<Typography variant='body2' color='text.secondary' mt={1}>
										This will not affect your cloud workspace.
										</Typography>
									</DialogContent>
									<DialogActions sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
										<Button color='error' variant='contained' onClick={wipeWorkspaceAndLoadDemo}>
										Wipe current workspace and load template
										</Button>
										<Typography variant='body2' color='text.secondary'>
										or
										</Typography>
										<Button variant='outlined' onClick={keepWorkspace}>
										Keep current data
										</Button>
									</DialogActions>
								</>
							)}
						</Dialog>
						<CookiePromptDialog />
						<Routes>
							<Route path="/settings" element={<ContentWrapper analyticsPage='/settings' repositoriesCollapse suppressIntroDialog showBackBtn><SettingsView /></ContentWrapper>} />
							<Route path="/settings/collections" element={<ContentWrapper analyticsPage='/settings/collections' repositoriesCollapse suppressIntroDialog showBackBtn backBtnTo='/settings'><CollectionsView /></ContentWrapper>} />
							<Route path="/settings/team" element={<ContentWrapper analyticsPage='/settings/team' repositoriesCollapse suppressIntroDialog showBackBtn backBtnTo='/settings'><TeamSettingsView /></ContentWrapper>} />
							<Route path="/settings/api" element={<ContentWrapper analyticsPage='/settings/api' repositoriesCollapse suppressIntroDialog showBackBtn backBtnTo='/settings'><ApiKeysView /></ContentWrapper>} />
							<Route path="/replicate/:repositoryId" element={<ContentWrapper suppressIntroDialog={true} repositoriesCollapse><ReplicateRepositoryView /></ContentWrapper>} />
							<Route path="/collection/:id" element={<ContentWrapper analyticsPage='/collection/:id' repositoriesCollapse noMarginTop={true}><RepositoryEntriesView /></ContentWrapper>} />
							<Route path="/collection/:id/settings" element={<ContentWrapper analyticsPage='/collection/:id/settings' repositoriesCollapse suppressIntroDialog={true}><RepositorySettingsView /></ContentWrapper>} />
							<Route path="/entries/:id" element={<ContentWrapper analyticsPage='/entries/:id' repositoriesCollapse><RepositoryEntryView /></ContentWrapper>} />
							<Route path="/timeline/:id" element={<ContentWrapper analyticsPage='/timeline/:id' repositoriesCollapse noMarginTop showBackBtnWithId><TimelineView /></ContentWrapper>} />
							<Route path="/chart/:id" element={<ContentWrapper analyticsPage='/chart/:id' repositoriesCollapse noMarginTop showBackBtnWithId><ChartView /></ContentWrapper>} />
							<Route path="/collection/:repositoryId/timeline" element={<ContentWrapper analyticsPage='/collection/:repositoryId/timeline' repositoriesCollapse noMarginTop><TimelineView /></ContentWrapper>} />
							<Route path={`${SHARED_VIEW_ROUTE_PREFIX}/collection/:id`} element={<ContentWrapper repositoriesCollapse noMarginTop suppressIntroDialog><RepositoryEntriesView /></ContentWrapper>} />
							<Route path={`${SHARED_VIEW_ROUTE_PREFIX}/entries/:id`} element={<ContentWrapper repositoriesCollapse suppressIntroDialog showSharedRepositoryBackBtn><RepositoryEntryView /></ContentWrapper>} />
							<Route path={`${SHARED_VIEW_ROUTE_PREFIX}/collection/:repositoryId/timeline`} element={<ContentWrapper repositoriesCollapse noMarginTop suppressIntroDialog><TimelineView /></ContentWrapper>} />
							<Route path={`${SHARED_VIEW_ROUTE_PREFIX}/timeline/:id`} element={<ContentWrapper repositoriesCollapse noMarginTop suppressIntroDialog><TimelineView /></ContentWrapper>} />
							<Route path="/repository/:id" element={<RepositoryRouteRedirect />} />
							<Route path="/:id?" element={<ContentWrapper analyticsPage='/:id?' noMarginTop={true} showBackBtnWithId showHintsBanner><CanvasView /></ContentWrapper>} />
							<Route path="*" element={<ContentWrapper repositoriesCollapse suppressIntroDialog><NotFoundView /></ContentWrapper>} />
						</Routes>
					</AppHeaderAndPromptsVisibilityProvider>
				</Router>
			</Page>
		</ThemeProvider>
	);
}

export default App;
